import type { ParsedReport } from './parseReport';
import { parseCarnavalReport } from './parseReport';

/**
 * Turning an uploaded .xlsx into rows.
 *
 * ExcelJS is loaded with a dynamic import so it is code-split: the parser only
 * arrives when somebody actually opens the upload modal, which is once a month
 * for two people. Everyone else — including suppliers on phones — never
 * downloads it.
 *
 * ExcelJS rather than SheetJS deliberately. The npm build of SheetJS is
 * abandoned at 0.18.5 and carries prototype-pollution and ReDoS advisories
 * with no fix available, and this code exists precisely to parse a file that
 * arrives from outside the Hub. A corrupt file would be enough to trigger
 * either; malice is not required.
 */

/** Cell values as ExcelJS reports them, flattened to what the parser expects. */
function cellValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return '';
  }
  // Formula cells arrive as { formula, result } — the result is the number a
  // human sees in the sheet, which is what a stock count means.
  if (typeof value === 'object' && 'result' in (value as Record<string, unknown>)) {
    return (value as { result: unknown }).result ?? '';
  }
  // Rich text arrives as { richText: [{ text }] }.
  if (typeof value === 'object' && 'richText' in (value as Record<string, unknown>)) {
    const parts = (value as { richText: { text?: string }[] }).richText;
    return parts.map((part) => part.text ?? '').join('');
  }
  if (typeof value === 'object' && 'text' in (value as Record<string, unknown>)) {
    return (value as { text: unknown }).text ?? '';
  }
  return value;
}

/**
 * Reads the first worksheet into an array of arrays.
 *
 * ExcelJS numbers rows and columns from 1 and omits empty leading cells, so
 * each row is rebuilt positionally — otherwise a blank first column would
 * shift every value left and quietly turn descriptions into SKUs.
 */
export async function readWorkbookRows(file: File): Promise<unknown[][]> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const sheet = workbook.worksheets[0];
  if (sheet === undefined) {
    throw new Error('inventory_no_sheet');
  }

  const rows: unknown[][] = [];
  sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const values: unknown[] = [];
    for (let column = 1; column <= sheet.columnCount; column++) {
      values.push(cellValue(row.getCell(column).value));
    }
    // rowNumber is 1-based; the parser expects zero-based indexes.
    rows[rowNumber - 1] = values;
  });

  // eachRow skips nothing with includeEmpty, but a sparse array would still
  // leave holes if the sheet starts oddly; fill them so indexing is honest.
  for (let i = 0; i < rows.length; i++) {
    rows[i] ??= [];
  }
  return rows;
}

/** The whole read: file in, checked report out. */
export async function readCarnavalReport(file: File): Promise<ParsedReport> {
  return parseCarnavalReport(await readWorkbookRows(file));
}
